import { useState, useMemo, useCallback } from "react";
import MapToolbar from "./components/MapToolbar";
import MapFilterPanel from "./components/MapFilterPanel";
import MapLegend from "./components/MapLegend";
import MapStatsCards from "./components/MapStatsCards";
import MapDetailPanel from "./components/MapDetailPanel";
import MapSearchBar from "./components/MapSearchBar";
import MapLayerControl from "./components/MapLayerControl";
import MapGISTools from "./components/MapGISTools";
import { arvoresMapa, areasVerdesMapa, ucsMapa, projetosAreasMapa, mapaLayers, mapStats } from "@/mocks/mapa";
import type { TreeMapPoint, AreaVerdeMap, UCMap, MapLayer } from "@/mocks/mapa";

const MAP_BOUNDS = {
  minLat: -16.724,
  maxLat: -16.705,
  minLng: -49.270,
  maxLng: -49.250,
};

function latLngToPercent(lat: number, lng: number): { top: number; left: number } {
  const top = ((MAP_BOUNDS.maxLat - lat) / (MAP_BOUNDS.maxLat - MAP_BOUNDS.minLat)) * 100;
  const left = ((lng - MAP_BOUNDS.minLng) / (MAP_BOUNDS.maxLng - MAP_BOUNDS.minLng)) * 100;
  return { top: Math.max(2, Math.min(98, top)), left: Math.max(2, Math.min(98, left)) };
}

function getRiskColor(risco: string): string {
  switch (risco) {
    case "baixo": return "bg-primary-500";
    case "medio": return "bg-caution-500";
    case "alto": return "bg-warning-500";
    case "critico": return "bg-danger-500";
    default: return "bg-foreground-400";
  }
}

function getTipoMarkerShape(tipo: string): string {
  if (tipo === "Palmeira") return "rounded-sm w-3 h-3";
  if (tipo === "Muda") return "w-2.5 h-2.5 border-2 border-primary-500 bg-white";
  return "rounded-full w-3.5 h-3.5";
}

export default function MapaArboreo() {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [activeLayers, setActiveLayers] = useState<MapLayer[]>(mapaLayers);
  const [filterPanelOpen, setFilterPanelOpen] = useState(true);
  const [legendOpen, setLegendOpen] = useState(true);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [layerControlOpen, setLayerControlOpen] = useState(false);
  const [clustersEnabled, setClustersEnabled] = useState(true);
  const [selectedTree, setSelectedTree] = useState<TreeMapPoint | null>(null);
  const [selectedArea, setSelectedArea] = useState<AreaVerdeMap | null>(null);
  const [selectedUC, setSelectedUC] = useState<UCMap | null>(null);
  const [searchResult, setSearchResult] = useState<string | null>(null);
  const [activeGISTool, setActiveGISTool] = useState<string | null>(null);
  const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>([]);

  const activeLayerIds = useMemo(
    () => new Set(activeLayers.filter((l) => l.ativa).map((l) => l.id)),
    [activeLayers]
  );

  const filteredTrees = useMemo(() => {
    return arvoresMapa.filter((tree) => {
      const layerMatch = tree.camada.some((c) => activeLayerIds.has(c));
      if (!layerMatch) return false;

      if (filters.projeto && tree.projeto !== filters.projeto) return false;
      if (filters.bairro && tree.bairro !== filters.bairro) return false;
      if (filters.logradouro && !tree.logradouro.toLowerCase().includes(filters.logradouro.toLowerCase())) return false;
      if (filters.tipo && tree.tipo !== filters.tipo) return false;
      if (filters.especie && tree.especie !== filters.especie) return false;
      if (filters.estadoFito && tree.estadoFitossanitario !== filters.estadoFito.toLowerCase()) return false;
      if (filters.classeRisco && tree.classeRisco !== filters.classeRisco) return false;
      if (filters.recomendacao && tree.recomendacao !== filters.recomendacao) return false;
      if (filters.statusRevisao && tree.statusRevisao !== filters.statusRevisao) return false;
      if (filters.tecnico && tree.tecnico !== filters.tecnico) return false;

      if (searchResult) {
        const sr = searchResult.toLowerCase();
        if (!tree.codigoCta.toLowerCase().includes(sr) &&
            !tree.nomePopular.toLowerCase().includes(sr) &&
            !tree.nomeCientifico.toLowerCase().includes(sr) &&
            !tree.logradouro.toLowerCase().includes(sr) &&
            !tree.bairro.toLowerCase().includes(sr)) return false;
      }

      if (activeQuickFilters.length > 0) {
        const matchesQuick = activeQuickFilters.some((qf) => {
          switch (qf) {
            case "critico": return tree.classeRisco === "critico";
            case "poda": return tree.recomendacao.toLowerCase().includes("poda");
            case "supressao": return tree.recomendacao.toLowerCase().includes("supressão");
            case "pendentes": return tree.statusRevisao === "em revisão" || tree.statusRevisao === "pendente de informação";
            default: return false;
          }
        });
        if (!matchesQuick) return false;
      }

      return true;
    });
  }, [filters, activeLayerIds, searchResult, activeQuickFilters]);

  const filteredAreasVerdes = useMemo(() => {
    return areasVerdesMapa.filter((a) => a.camada.some((c) => activeLayerIds.has(c)));
  }, [activeLayerIds]);

  const filteredUCs = useMemo(() => {
    return ucsMapa.filter((u) => u.camada.some((c) => activeLayerIds.has(c)));
  }, [activeLayerIds]);

  const filteredProjetos = useMemo(() => {
    return projetosAreasMapa.filter((p) => p.camada.some((c) => activeLayerIds.has(c)));
  }, [activeLayerIds]);

  const handleApplyFilters = useCallback((newFilters: Record<string, string>) => {
    setFilters(newFilters);
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters({});
    setSearchResult(null);
    setActiveQuickFilters([]);
  }, []);

  const handleToggleLayer = useCallback((layerId: string) => {
    setActiveLayers((prev) =>
      prev.map((l) => (l.id === layerId ? { ...l, ativa: !l.ativa } : l))
    );
  }, []);

  const handleTreeClick = useCallback((tree: TreeMapPoint) => {
    setSelectedTree(tree);
    setSelectedArea(null);
    setSelectedUC(null);
    setDetailPanelOpen(true);
  }, []);

  const handleAreaClick = useCallback((area: AreaVerdeMap) => {
    setSelectedArea(area);
    setSelectedTree(null);
    setSelectedUC(null);
    setDetailPanelOpen(true);
  }, []);

  const handleUCClick = useCallback((uc: UCMap) => {
    setSelectedUC(uc);
    setSelectedTree(null);
    setSelectedArea(null);
    setDetailPanelOpen(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailPanelOpen(false);
    setSelectedTree(null);
    setSelectedArea(null);
    setSelectedUC(null);
  }, []);

  const handleSearch = useCallback((query: string) => {
    setSearchResult(query);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchResult(null);
  }, []);

  const handleQuickFilter = useCallback((filter: string) => {
    setActiveQuickFilters((prev) =>
      prev.includes(filter) ? prev.filter((f) => f !== filter) : [...prev, filter]
    );
  }, []);

  const handleGISTool = useCallback((tool: string) => {
    setActiveGISTool((prev) => (prev === tool ? null : tool));
  }, []);

  const riskStats = useMemo(() => {
    const critical = filteredTrees.filter((t) => t.classeRisco === "critico").length;
    const poda = filteredTrees.filter((t) =>
      t.recomendacao.toLowerCase().includes("poda")
    ).length;
    return { critical, poda };
  }, [filteredTrees]);

  const visibleQuickFilters = useMemo(() => {
    const critical = filteredTrees.filter((t) => t.classeRisco === "critico").length;
    const poda = filteredTrees.filter((t) => t.recomendacao.toLowerCase().includes("poda")).length;
    const supressao = filteredTrees.filter((t) => t.recomendacao.toLowerCase().includes("supressão")).length;
    const pendentes = filteredTrees.filter((t) => t.statusRevisao === "em revisão" || t.statusRevisao === "pendente de informação").length;
    return [
      { key: "all", label: "Todas", count: filteredTrees.length },
      { key: "critico", label: "Risco crítico", count: critical, color: "text-danger-600" },
      { key: "poda", label: "Poda recomendada", count: poda, color: "text-warning-600" },
      { key: "supressao", label: "Supressão", count: supressao, color: "text-danger-700" },
      { key: "pendentes", label: "Pendentes", count: pendentes, color: "text-caution-600" },
    ];
  }, [filteredTrees]);

  // Mock clusters for visual effect
  const clusters = useMemo(() => {
    if (!clustersEnabled) return [];
    const clusterPoints = [
      { lat: -16.7135, lng: -49.2590, count: 18 },
      { lat: -16.7155, lng: -49.2620, count: 12 },
      { lat: -16.7178, lng: -49.2650, count: 24 },
      { lat: -16.7195, lng: -49.2670, count: 7 },
      { lat: -16.7110, lng: -49.2570, count: 15 },
      { lat: -16.7210, lng: -49.2685, count: 10 },
    ];
    return clusterPoints;
  }, [clustersEnabled]);

  const hasActiveFilters = Object.keys(filters).length > 0 || !!searchResult || activeQuickFilters.length > 0;

  return (
    <div className="flex flex-col h-[calc(100vh-0px)] overflow-hidden">
      {/* Toolbar */}
      <MapToolbar
        totalArvores={arvoresMapa.length}
        filteredCount={filteredTrees.length}
        onNewTree={() => {}}
        onExportGeoJSON={() => {}}
        onCenterMap={() => {}}
        onToggleLayers={() => setLayerControlOpen(!layerControlOpen)}
        layersOpen={layerControlOpen}
      />

      {/* Main content: 3 panels */}
      <div className="flex flex-1 overflow-hidden relative">
        {/* Left panel: Filters */}
        <MapFilterPanel
          collapsed={!filterPanelOpen}
          onToggle={() => setFilterPanelOpen(!filterPanelOpen)}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          onSaveView={() => {}}
          onExportSelection={() => {}}
          onGenerateReport={() => {}}
        />

        {/* Center: Map */}
        <div className="flex-1 relative overflow-hidden bg-background-100">
          {/* Google Maps iframe as base layer */}
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3824.027175581527!2d-49.270!3d-16.7145!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x935ef0e04c4cd7c7%3A0x5b557e9182f26e47!2sSetor+Bela+Vista%2C+Goi%C3%A2nia+-+GO!5e0!3m2!1spt-BR!2sbr!4v1751300000000!5m2!1spt-BR!2sbr&maptype=roadmap"
            className="absolute inset-0 w-full h-full border-0"
            title="Mapa Arbóreo — Setor Bela Vista, Goiânia"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          ></iframe>

          {/* Dark overlay for contrast */}
          <div className="absolute inset-0 bg-black/5 pointer-events-none"></div>

          {/* Map overlay - markers and polygons */}
          <div className="absolute inset-0">
            {/* Area polygons - Areas Verdes */}
            {filteredAreasVerdes.map((area) => {
              const center = latLngToPercent(area.latitude, area.longitude);
              return (
                <div
                  key={area.id}
                  className="absolute cursor-pointer group"
                  style={{ top: `${center.top - 5}%`, left: `${center.left - 4}%`, width: "8%", height: "6%" }}
                  onClick={() => handleAreaClick(area)}
                  title={area.nome}
                >
                  <div className="w-full h-full rounded-md bg-accent-400/20 border-2 border-accent-400/60 flex items-center justify-center transition-all group-hover:bg-accent-400/30">
                    <span className="text-[8px] font-semibold text-accent-700 bg-white/80 px-1 rounded whitespace-nowrap">
                      {area.nome.length > 18 ? area.nome.substring(0, 18) + "..." : area.nome}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* Area polygons - UCs */}
            {filteredUCs.map((uc) => {
              const center = latLngToPercent(uc.latitude, uc.longitude);
              return (
                <div
                  key={uc.id}
                  className="absolute cursor-pointer group"
                  style={{ top: `${center.top - 4}%`, left: `${center.left - 3.5}%`, width: "7%", height: "5%" }}
                  onClick={() => handleUCClick(uc)}
                  title={uc.nome}
                >
                  <div className="w-full h-full rounded-md bg-accent-600/15 border-2 border-accent-500/50 border-dashed flex items-center justify-center transition-all group-hover:bg-accent-600/25">
                    <span className="text-[8px] font-semibold text-accent-600 bg-white/80 px-1 rounded whitespace-nowrap">
                      {uc.nome.length > 16 ? uc.nome.substring(0, 16) + "..." : uc.nome}
                    </span>
                  </div>
                </div>
              );
            })}

            {/* Area polygons - Projetos */}
            {filteredProjetos.map((proj) => {
              const center = latLngToPercent(proj.latitude, proj.longitude);
              return (
                <div
                  key={proj.id}
                  className="absolute pointer-events-none"
                  style={{ top: `${center.top - 5}%`, left: `${center.left - 7}%`, width: "14%", height: "7%" }}
                >
                  <div className="w-full h-full rounded-md bg-primary-400/10 border border-primary-400/30 border-dashed"></div>
                  <span className="absolute top-1 left-1 text-[8px] font-medium text-primary-600 bg-white/80 px-1 rounded whitespace-nowrap">
                    {proj.nome.length > 20 ? proj.nome.substring(0, 20) + "..." : proj.nome}
                  </span>
                </div>
              );
            })}

            {/* Clusters */}
            {clustersEnabled && clusters.map((cluster, idx) => {
              const pos = latLngToPercent(cluster.lat, cluster.lng);
              return (
                <div
                  key={`cluster-${idx}`}
                  className="absolute cursor-pointer z-10"
                  style={{ top: `${pos.top - 2}%`, left: `${pos.left - 2}%` }}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs shadow-lg transition-transform hover:scale-110 ${
                    cluster.count > 15 ? "bg-warning-500" : cluster.count > 10 ? "bg-accent-500" : "bg-primary-500"
                  }`}>
                    {cluster.count}
                  </div>
                </div>
              );
            })}

            {/* Tree markers */}
            {filteredTrees.map((tree) => {
              const pos = latLngToPercent(tree.latitude, tree.longitude);
              const isSelected = selectedTree?.id === tree.id;
              const highlightClass = tree.classeRisco === "critico" ? "animate-pulse" : "";

              return (
                <button
                  key={tree.id}
                  className={`absolute cursor-pointer group z-20 transition-transform ${
                    isSelected ? "scale-150 z-30" : "hover:scale-125"
                  } ${highlightClass}`}
                  style={{ top: `${pos.top - 0.8}%`, left: `${pos.left - 0.8}%` }}
                  onClick={() => handleTreeClick(tree)}
                  title={`${tree.nomePopular} - ${tree.codigoCta}`}
                  aria-label={`${tree.nomePopular}, ${tree.classeRisco === "critico" ? "risco crítico" : tree.classeRisco === "alto" ? "risco alto" : "risco " + tree.classeRisco}`}
                >
                  {/* Marker glow for selected */}
                  {isSelected && (
                    <div className="absolute inset-0 rounded-full bg-accent-400/40 animate-ping w-5 h-5 -m-0.5"></div>
                  )}

                  <div className={`${getTipoMarkerShape(tree.tipo)} ${getRiskColor(tree.classeRisco)} ${
                    tree.classeRisco === "critico" ? "ring-4 ring-danger-200" : "ring-2 ring-white"
                  } shadow-md relative`}>
                    {/* Tooltip on hover */}
                    <div className="absolute left-full ml-1.5 top-1/2 -translate-y-1/2 bg-white border border-background-200 rounded-md px-2 py-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-40 min-w-[120px]">
                      <p className="text-[10px] font-semibold text-foreground-900">{tree.nomePopular}</p>
                      <p className="text-[9px] text-foreground-500 font-mono">{tree.codigoCta}</p>
                      <p className="text-[9px] text-foreground-600">{tree.bairro}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Floating UI elements on map */}

          {/* Search bar - top center */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20 w-80">
            <MapSearchBar onSearch={handleSearch} onClear={handleClearSearch} />
          </div>

          {/* Quick filter pills */}
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 flex gap-1.5">
            {visibleQuickFilters.map((qf) => (
              <button
                key={qf.key}
                onClick={() => {
                  if (qf.key === "all") {
                    setActiveQuickFilters([]);
                  } else {
                    handleQuickFilter(qf.key);
                  }
                }}
                className={`text-[10px] font-medium px-2.5 py-1.5 rounded-full border transition-colors whitespace-nowrap ${
                  (qf.key === "all" && activeQuickFilters.length === 0) || activeQuickFilters.includes(qf.key)
                    ? "bg-accent-500 text-white border-accent-500"
                    : "bg-white/90 text-foreground-600 border-background-200 hover:bg-white"
                } shadow-sm`}
              >
                {qf.label}
                <span className="ml-1 opacity-70">({qf.count})</span>
              </button>
            ))}
          </div>

          {/* GIS Tools - left side */}
          <div className="absolute top-3 left-3 z-20">
            <MapGISTools
              onMeasureDistance={() => handleGISTool("distance")}
              onMeasureArea={() => handleGISTool("area")}
              onDrawPolygon={() => handleGISTool("polygon")}
              onSelectArea={() => handleGISTool("select")}
              onMyLocation={() => handleGISTool("location")}
              onToggleClusters={() => setClustersEnabled(!clustersEnabled)}
              clustersEnabled={clustersEnabled}
            />
          </div>

          {/* Active GIS tool indicator */}
          {activeGISTool && (
            <div className="absolute top-3 left-14 z-20 bg-warning-500 text-white text-[10px] font-medium px-2.5 py-1 rounded-full shadow-md whitespace-nowrap">
              <i className="ri-tools-line mr-1" aria-hidden="true"></i>
              {activeGISTool === "distance" && "Medindo distância..."}
              {activeGISTool === "area" && "Medindo área..."}
              {activeGISTool === "polygon" && "Desenhando polígono..."}
              {activeGISTool === "select" && "Selecionando área..."}
              {activeGISTool === "location" && "Centralizando..."}
              <button onClick={() => setActiveGISTool(null)} className="ml-2 hover:opacity-70">
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}

          {/* No results message */}
          {filteredTrees.length === 0 && hasActiveFilters && (
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 bg-white/95 backdrop-blur-sm border border-background-200 rounded-card p-6 text-center shadow-lg">
              <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-search-line text-foreground-400 text-xl" aria-hidden="true"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-800 font-heading">Nenhum resultado encontrado</p>
              <p className="text-xs text-foreground-500 mt-1">Ajuste os filtros ou a busca para ver mais árvores.</p>
              <button onClick={handleClearFilters} className="mt-3 btn-outline text-xs py-1.5 px-4 whitespace-nowrap">
                Limpar todos os filtros
              </button>
            </div>
          )}

          {/* Active filters indicator */}
          {hasActiveFilters && filteredTrees.length > 0 && (
            <div className="absolute top-28 left-1/2 -translate-x-1/2 z-20 bg-primary-500 text-white text-[10px] font-medium px-3 py-1.5 rounded-full shadow-md whitespace-nowrap">
              <i className="ri-filter-3-line mr-1" aria-hidden="true"></i>
              {filteredTrees.length} árvores encontradas com os filtros atuais
              <button onClick={handleClearFilters} className="ml-2 hover:opacity-70 underline">
                Limpar
              </button>
            </div>
          )}

          {/* Layer Control overlay */}
          <div className="absolute top-3 right-3 z-20">
            <MapLayerControl
              layers={activeLayers}
              onToggleLayer={handleToggleLayer}
              open={layerControlOpen}
              onClose={() => setLayerControlOpen(false)}
            />
          </div>

          {/* Stats cards - bottom center */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20">
            <MapStatsCards
              arvoresVisiveis={filteredTrees.length}
              riscoCritico={riskStats.critical}
              podaRecomendada={riskStats.poda}
              areasVerdes={filteredAreasVerdes.length}
              ucs={filteredUCs.length}
            />
          </div>

          {/* Legend - bottom right */}
          <div className="absolute bottom-3 right-3 z-20">
            <MapLegend collapsed={!legendOpen} onToggle={() => setLegendOpen(!legendOpen)} />
          </div>
        </div>

        {/* Right panel: Detail */}
        <MapDetailPanel
          selectedTree={selectedTree}
          selectedArea={selectedArea}
          selectedUC={selectedUC}
          open={detailPanelOpen}
          onClose={handleCloseDetail}
        />
      </div>
    </div>
  );
}